import React from 'react';
import PropTypes from 'prop-types';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { groupBy, sortBy } from 'lodash';
import { Callout, DirectionalHint } from 'office-ui-fabric-react';
import Link from '../Link';
import styles from './AutosuggestDropdown.style';
import AutosuggestDropdownItem from '../AutosuggestDropdownItem';

/**
 * This is needed to create sections in the list. We use the header property
 * from the suggestions to create the sections. This allows use to separate
 * things like Products and Product Family sections in the product dropdown.
 */
function createSectionedList(suggestions, headerOrder = []) {
  const sections = groupBy(suggestions, 'header');
  const sectionKeys = sortBy(
    Object.keys(sections),
    key => headerOrder.includes(key),
    key => headerOrder.indexOf(key),
    key => key,
  );

  if (sectionKeys.length === 1) {
    return suggestions.map(suggestion => ({
      type: 'row',
      suggestion,
    }));
  }

  const list = [];

  sectionKeys.forEach(sectionKey => {
    const sectionList = sections[sectionKey];

    list.push({ type: 'header', title: sectionKey });

    sectionList.forEach(suggestion => {
      list.push({
        type: 'row',
        suggestion,
      });
    });
  });

  return list;
}

const AutosuggestDropdown = ({
  target,
  isLoading,
  viewMore,
  suggestions,
  noResultsText,
  onClick,
  history,
  onRenderSuggestion,
  calloutOnDismiss,
  error = null,
  className: classNameProp,
  headerOrder = [],
  ...other
}) => {
  const renderSuggestions = () => {
    const sectionedList = createSectionedList(suggestions, headerOrder);

    return (
      <>
        {sectionedList.map(listItem => {
          // maybe add a onRenderHeader in the future if needed?
          if (listItem.type === 'header') {
            return (
              <div className={styles.sectionHeader} key={listItem.title}>
                {listItem.title}
              </div>
            );
          }

          return onRenderSuggestion({ ...listItem.suggestion, onClick, history });
        })}
      </>
    );
  };

  return (
    <Callout
      target={`#${target}`}
      directionalHint={DirectionalHint.bottomLeftEdge}
      isBeakVisible={false}
      gapSpace={0}
      onDismiss={calloutOnDismiss}
    >
      <Stack data-testid="autosuggestDropdown" {...other} className={classNameProp}>
        {isLoading && <Spinner size={SpinnerSize.small} className={styles.loadingSpinner} />}
        {!isLoading && suggestions.length && renderSuggestions()}

        {!isLoading && !suggestions.length && (
          <div className={styles.noResultsContainer}>
            <Text className={styles.noResultsLabel}>No results</Text>
            {noResultsText && <Text className={styles.noResultsDescription}>{noResultsText}</Text>}
          </div>
        )}

        {viewMore && (
          <Stack>
            <Link className={styles.moreResults} href={viewMore.href}>
              {viewMore.text}
            </Link>
          </Stack>
        )}
      </Stack>
      {error && <div className={styles.errorContainer}>{error}</div>}
    </Callout>
  );
};

AutosuggestDropdown.propTypes = {
  /**
   * The suggestions you'd like to show in the search results suggestion dropdown. If nothing
   * is passed for these, then the dropdown will never appear.
   */
  target: PropTypes.string.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      value: PropTypes.node,
      label: PropTypes.node.isRequired,
      secondaryLabel: PropTypes.node,
      tertiaryLabel: PropTypes.node,
      href: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  calloutOnDismiss: PropTypes.func,
  onRenderSuggestion: PropTypes.func,
  viewMore: PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  className: PropTypes.string,
  noResultsText: PropTypes.string,
  error: PropTypes.string,
  headerOrder: PropTypes.arrayOf(PropTypes.string),
};

AutosuggestDropdown.defaultProps = {
  className: '',
  onClick: null,
  calloutOnDismiss: null,
  viewMore: null,
  history: null,
  noResultsText: undefined,
  error: null,
  onRenderSuggestion: AutosuggestDropdownItem,
  headerOrder: [],
};

export default AutosuggestDropdown;
